import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import TrainingDetailModal from './components/modal-detail.vue'
import toast from '@/utils/toast'
import { fetchCourseCalendarData } from './data/api'
import { watch } from 'vue'

export default {
    name: 'TrainingCalendar',

    components: {
        FullCalendar,
        TrainingDetailModal
    },

    data() {
        return {
            /** =====================
             * CALENDAR DATA
             ====================== */
            events: [],

            calendarOptions: {
                plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
                initialView: 'dayGridMonth',
                headerToolbar: {
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                },
                eventClick: this.handleEventClick,
                datesSet: this.handleDatesSet,
                eventTimeFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                },
                height: 'auto',
                eventDisplay: 'block',
                displayEventTime: true,
                displayEventEnd: true,
            },

            /** =====================
             * FILTERS
             ====================== */
            filters: {
                start_date: null,
                end_date: null,
                course_type: '',
                training_provider: '',
                training_type: '',
            },

            isLoading: false,

            /** =====================
             * FILTER OPTIONS
             ====================== */
            providerOptions: [
                { value: '', text: 'All Providers' },
                { value: 'Ugems University', text: 'GEMS Univ' },
                { value: 'My Learning', text: 'BIB Academy' },
                { value: 'Other', text: 'Other' }
            ],
            courseTypeOptions: [
                { value: '', text: 'All Course Types' },
                { value: 'C', text: 'Certification' },
                { value: 'SC', text: 'Semi Certification' },
                { value: 'T', text: 'Training' }
            ],
            trainingTypeOptions: [
                { value: '', text: 'All Training Types' },
                { value: 'structured_learning', text: 'Structured Learning' },
                { value: 'social_learning', text: 'Social Learning' },
                { value: 'experiential_learning', text: 'Experiential Learning' }
            ],

            /** =====================
             * MODAL
             ====================== */
            showDetailModal: false,
            selectedEvent: null,
        }
    },

    mounted() {
        // inject events ke calendar sekali
        // this.calendarOptions.events = this.events
    },

    computed: {
        activeFilters() {
            const filters = []

            if (this.filters.training_provider) {
                filters.push({
                    key: 'training_provider',
                    label: 'Training Provider',
                    values: [this.filters.training_provider]
                })
            }

            if (this.filters.course_type) {
                const opt = this.courseTypeOptions.find(
                    o => o.value === this.filters.course_type
                )
                filters.push({
                    key: 'course_type',
                    label: 'Course Type',
                    values: [opt ? opt.text : this.filters.course_type]
                })
            }

            if (this.filters.training_type) {
                const opt = this.trainingTypeOptions.find(
                    o => o.value === this.filters.training_type
                )
                filters.push({
                    key: 'training_type',
                    label: 'Training Type',
                    values: [opt ? opt.text : this.filters.training_type]
                })
            }

            return filters
        },

        activeFiltersCount() {
            return this.activeFilters.length
        }
    },

    methods: {
        /** =====================
         * HELPERS
         ====================== */
        formatDate(date) {
            if (!date) return null
            const d = new Date(date)
            if (isNaN(d)) return null

            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        },

        buildQueryFromFilters() {
            const raw = {
                start_date: this.filters.start_date,
                end_date: this.filters.end_date,
                course_type: this.filters.course_type,
                training_provider: this.filters.training_provider,
                training_type: this.filters.training_type,
            }

            return Object.fromEntries(
                Object.entries(raw).filter(([_, v]) => v)
            )
        },

        /** =====================
         * DATA FETCH
         ====================== */
        async loadEvents() {
            try {
                this.isLoading = true
                const query = this.buildQueryFromFilters()

                const data = await fetchCourseCalendarData(query)
                const events = this.transformToCalendarEvents(data || [])


                // 🔥 UPDATE VIA API (BUKAN options)
                const calendarApi = this.$refs.calendarRef?.getApi()
                if (calendarApi) {
                    calendarApi.removeAllEvents()
                    calendarApi.addEventSource(events)
                }
            } catch (error) {
                const msg =
                    error?.response?.data?.error ||
                    error?.message ||
                    'Event pelatihan belum berhasil dimuat'

                toast({ type: 'error', title: 'Error', message: msg })
            } finally {
                this.isLoading = false
            }
        },

        /** =====================
         * TRANSFORM
         ====================== */
        transformToCalendarEvents(trainings) {
            return trainings.map(t => {
                let backgroundColor = '#6C757D' // default gray
                let borderColor = '#5A6268'

                // Priority: Mandatory > Training Type
                if (t.isMandatory) {
                    backgroundColor = '#DC3545' // Red for mandatory
                    borderColor = '#BD2130'
                } else {
                    // Color based on training type
                    switch (t.trainingType) {
                        case 'structured_learning':
                            backgroundColor = '#0066CC' // Blue
                            borderColor = '#0052A3'
                            break
                        case 'social_learning':
                            backgroundColor = '#28A745' // Green
                            borderColor = '#218838'
                            break
                        case 'experiential_learning':
                            backgroundColor = '#FFC107' // Yellow/Orange
                            borderColor = '#E0A800'
                            break
                        default:
                            backgroundColor = '#6C757D' // Gray
                            borderColor = '#5A6268'
                    }
                }

                return {
                    id: t.id,
                    title: t.courseName,
                    start: t.startDate,
                    end: t.endDate,
                    backgroundColor,
                    borderColor,
                    textColor: '#FFFFFF',
                    extendedProps: t
                }
            })
        },

        /** =====================
         * CALENDAR HANDLERS
         ====================== */
        handleDatesSet(dateInfo) {
            const start = this.formatDate(dateInfo.view.currentStart)
            const end = this.formatDate(dateInfo.view.currentEnd)

            // 🛑 guard anti double fetch
            if (
                this.filters.start_date === start &&
                this.filters.end_date === end
            ) {
                return
            }

            this.filters.start_date = start
            this.filters.end_date = end

            this.loadEvents()
        },

        handleEventClick(info) {
            this.selectedEvent = info.event.extendedProps
            this.showDetailModal = true
        },

        /** =====================
         * FILTER ACTIONS
         ====================== */
        applyFilters() {
            this.loadEvents()
        },

        resetFilters() {
            this.filters.course_type = ''
            this.filters.training_provider = ''
            this.filters.training_type = ''
            this.loadEvents()
        },

        removeFilter(key) {
            this.filters[key] = ''
            this.loadEvents()
        },

        /** =====================
         * MODAL
         ====================== */
        closeDetailModal() {
            this.showDetailModal = false
            this.selectedEvent = null
        }
    }
}
